/**
 * PanelHandle
 * -----------
 * Single open/close affordance for the side / bottom panel. Replaces
 * the older Hud "open list view" button and provides one consistent
 * tap target whether the panel anchors on the right (desktop, landscape)
 * or at the bottom (mobile portrait).
 *
 * Layout responsibility is split with `useResponsivePanelLayout` —
 * that hook owns where the panel itself lives, this component
 * positions the handle relative to the panel boundary:
 *
 *   SIDE mode (desktop / landscape)
 *     - Closed: handle clings to the right edge of the viewport.
 *       Chevron points LEFT ("pull me open").
 *     - Open: handle clings to the panel's left edge (the canvas/panel
 *       boundary). Chevron points RIGHT ("push me closed").
 *     - Smooth-drag tracking via `right: var(--bjjmap-drawer-width)`
 *       which ResizeHandle overrides per-frame during a drag.
 *
 *   BOTTOM-SHEET mode (mobile portrait)
 *     - Closed: handle sits at the BOTTOM edge of the viewport,
 *       centered horizontally. Chevron points UP ("pull me up").
 *     - Open: handle sits at the TOP edge of the bottom sheet
 *       (the canvas/sheet boundary), centered. Chevron points DOWN
 *       ("push me down").
 *     - Position tracks the panel's height via
 *       `bottom: var(--bjjmap-sheet-height)`; that variable can be set
 *       to 0 (closed) or the sheet height (open) so the handle slides
 *       with the boundary in lockstep.
 *
 * z-index sits *below* the drawer's resize handle (60) so when the
 * caret visually overlaps the resize line's hit zone, the resize
 * cursor still wins and the user can grab the line to drag the
 * boundary. The remaining width of the button is still clickable
 * as the collapse/open affordance.
 */
import { useEffect } from 'react';
import type { CSSProperties } from 'react';
import { useAppStore } from '../state/store';
import { PANEL_OPEN_MS, PANEL_EASE } from './useResponsivePanelLayout';
import { useViewport } from './useViewport';

export function PanelHandle() {
  const drawerOpen = useAppStore((s) => s.drawerOpen);
  const listViewOpen = useAppStore((s) => s.listViewOpen);
  const gameplanViewOpen = useAppStore((s) => s.gameplanViewOpen);
  const drawerWidth = useAppStore((s) => s.drawerWidth);
  const selection = useAppStore((s) => s.selection);
  const setDrawerOpen = useAppStore((s) => s.setDrawerOpen);
  const setListViewOpen = useAppStore((s) => s.setListViewOpen);
  const setGameplanViewOpen = useAppStore((s) => s.setGameplanViewOpen);
  const drawerResizing = useAppStore((s) => s.drawerResizing);

  const { isMobile, isPortrait } = useViewport();
  const bottomSheet = isMobile && isPortrait;
  const open = drawerOpen || listViewOpen || gameplanViewOpen;

  // Keep the side-drawer CSS variable in sync with the ACTUAL space
  // the panel occupies in side mode. ResizeHandle's per-frame
  // onFrame callback overrides this during drag; this useEffect
  // restores the committed value once the drag releases.
  //
  // The value reflects three things that the previous "always
  // ${drawerWidth}px" seeding got wrong, leaving EdgeActions
  // floating where no panel actually was:
  //   - Drawer closed → variable is 0px (no panel to track).
  //   - Bottom-sheet mode (mobile portrait) → variable is 0px
  //     (there's a sheet at the bottom, not a side panel; the
  //     sheet's contribution lives in `--bjjmap-sheet-height`).
  //   - Drawer open in side mode → variable is
  //     min(${drawerWidth}px, 96vw), matching the cap that
  //     useResponsivePanelLayout applies to the actual panel.
  //     Otherwise on a narrow viewport the variable would over-
  //     report the panel's width and EdgeActions would land off
  //     the boundary.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const value =
      open && !bottomSheet
        ? `min(${drawerWidth}px, 96vw)`
        : '0px';
    document.documentElement.style.setProperty(
      '--bjjmap-drawer-width',
      value,
    );
  }, [drawerWidth, open, bottomSheet]);

  // Mirror the open state + LIVE sheet height into a CSS variable so
  // the handle slides with the sheet's animation AND with user drags.
  // Previously seeded with PANEL_SHEET_HEIGHT_FALLBACK (a CSS literal
  // string "min(50vh, 480px)"), which desynced the handle from the
  // actual store-driven sheet height — leaving the caret detached
  // from the panel after a user-drag + close + re-open cycle. Reading
  // the live store value here keeps them locked together.
  const sheetHeight = useAppStore((s) => s.sheetHeight);
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.style.setProperty(
      '--bjjmap-sheet-height',
      open ? `${sheetHeight}px` : '0px',
    );
  }, [open, sheetHeight]);

  /**
   * Click handler: toggle the panel set. When opening, prefer the Detail
   * Drawer if there's a selection; otherwise fall back to the List view.
   * We never auto-open the Gameplan view — that's user-initiated only.
   */
  const onClick = () => {
    if (open) {
      setDrawerOpen(false);
      setListViewOpen(false);
      setGameplanViewOpen(false);
      return;
    }
    if (selection.kind !== 'none') {
      setDrawerOpen(true);
    } else {
      setListViewOpen(true);
    }
  };

  // Choose orientation-appropriate positioning and visual.
  const style: CSSProperties = bottomSheet
    ? {
        // Centered horizontal pill at the canvas/sheet boundary.
        position: 'fixed',
        left: '50%',
        bottom: `var(--bjjmap-sheet-height, 0px)`,
        transform: 'translateX(-50%)',
        width: 60,
        height: 18,
        borderRadius: '8px 8px 0 0',
        borderBottom: 'none',
        // Width transition keeps the pill stable as the sheet animates.
        transition: drawerResizing
          ? 'none'
          : `bottom ${PANEL_OPEN_MS}ms ${PANEL_EASE}, background 120ms ease, color 120ms ease`,
      }
    : {
        // Vertical bar at the canvas/side-panel boundary.
        position: 'fixed',
        top: '50%',
        right: open ? `var(--bjjmap-drawer-width, ${drawerWidth}px)` : 0,
        transform: 'translateY(-50%)',
        width: 20,
        height: 60,
        borderRadius: '8px 0 0 8px',
        borderRight: 'none',
        transition: drawerResizing
          ? 'none'
          : `right 220ms ${PANEL_EASE}, background 120ms ease, color 120ms ease`,
      };

  return (
    <button
      onClick={onClick}
      aria-label={open ? 'Close panel' : 'Open panel'}
      title={
        open
          ? 'Close panel'
          : selection.kind === 'none'
            ? 'Open list view'
            : 'Open detail'
      }
      style={{
        ...style,
        background: 'rgba(20,22,30,0.85)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        border: '1px solid rgba(255,255,255,0.10)',
        color: '#cad3df',
        fontSize: 16,
        lineHeight: 1,
        cursor: 'pointer',
        // Below the resize handle so its drag wins at the boundary.
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 0,
      }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background =
          'rgba(40,44,58,0.92)';
        (e.currentTarget as HTMLButtonElement).style.color = '#ffffff';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background =
          'rgba(20,22,30,0.85)';
        (e.currentTarget as HTMLButtonElement).style.color = '#cad3df';
      }}
    >
      {/* Direction glyph — the chevron points TOWARD the action.
          Switched from Unicode chevrons (⌃ / ⌄ / ‹ / ›) to inline
          SVG: those characters have inconsistent vertical metrics
          across fonts (the visible glyph hugs the top OR bottom of
          its EM box depending on the codepoint), which is why the
          tip used to touch the top of the pill when collapsed and
          the bottom when expanded. SVG centers its viewBox in the
          flex container with no font-metrics ambiguity. */}
      <Chevron direction={bottomSheet
        ? (open ? 'down' : 'up')
        : (open ? 'right' : 'left')}
      />
    </button>
  );
}

/** Inline chevron — 12×12 viewBox so the visible path occupies the
 *  middle 8 units and `display: flex; align-items: center;
 *  justify-content: center` on the parent button lands it
 *  geometrically centered regardless of font. */
function Chevron({ direction }: { direction: 'up' | 'down' | 'left' | 'right' }) {
  // All four paths trace a V starting and ending at the same y
  // (centered vertically in the 12-unit box), so rotating just
  // means swapping which endpoint is the tip. We hand-write each
  // path rather than CSS-rotating because rotation snaps the
  // visible glyph back to the geometric center every time.
  const d = {
    up:    'M2 8l4-4 4 4',
    down:  'M2 4l4 4 4-4',
    left:  'M8 2L4 6l4 4',
    right: 'M4 2l4 4-4 4',
  }[direction];
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d={d} />
    </svg>
  );
}
